import React, { useState, useEffect } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  User
} from 'firebase/auth';
import { auth } from '../firebase';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAuthSuccess: (user: User) => void;
}

// Firebase's raw messages read like "Firebase: Error (auth/invalid-credential)."
export function authErrorMessage(err: any): string {
  switch (err?.code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
    case 'auth/invalid-login-credentials':
      return 'That email and password do not match. Check them, or use "Forgot password".';
    case 'auth/invalid-email':
      return 'That email address is not valid.';
    case 'auth/email-already-in-use':
      return 'An account already exists for this email. Sign in instead, or reset the password.';
    case 'auth/weak-password':
      return 'Choose a password of at least 6 characters.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Wait a few minutes, or reset your password.';
    case 'auth/network-request-failed':
      return 'Could not reach the server. Check your connection and try again.';
    case 'auth/user-disabled':
      return 'This account has been disabled. Contact support.';
    default:
      return err?.message ? String(err.message).replace(/^Firebase:\s*/, '') : 'Something went wrong. Please try again.';
  }
}

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, onAuthSuccess }) => {
  const [mode, setMode] = useState<'login' | 'signup' | 'reset'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetSent, setResetSent] = useState(false);

  // Start fresh every time the dialog opens. The component stays mounted, so
  // after signing out it reopened in its last mode with the previous email AND
  // password still in the fields (a shared-computer leak).
  useEffect(() => {
    if (!isOpen) return;
    setMode('login');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setError('');
    setResetSent(false);
    setLoading(false);
  }, [isOpen]);

  // Close on Escape (accessibility / standard modal behavior)
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (mode === 'reset') {
        await sendPasswordResetEmail(auth, email);
        setResetSent(true);
        setLoading(false);
        return;
      }

      if (mode === 'signup' && password !== confirmPassword) {
        setError('Passwords do not match');
        setLoading(false);
        return;
      }

      const userCredential = mode === 'login'
        ? await signInWithEmailAndPassword(auth, email, password)
        : await createUserWithEmailAndPassword(auth, email, password);

      onAuthSuccess(userCredential.user);
      onClose();
    } catch (err: any) {
      setError(authErrorMessage(err));
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="card p-6 w-full max-w-md mx-4 animate-slide-up"
        role="dialog"
        aria-modal="true"
        aria-label="Authentication"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-xl font-semibold text-content">
            {mode === 'login' ? 'Sign In' : mode === 'signup' ? 'Sign Up' : 'Reset Password'}
          </h2>
          <button
            onClick={onClose}
            className="text-content-tertiary hover:text-content p-1.5 rounded-lg hover:bg-surface-overlay transition-colors"
            aria-label="Close"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {resetSent ? (
          <div className="text-center py-6">
            <div className="w-12 h-12 rounded-full bg-green-500/10 border border-green-500/20 flex items-center justify-center mx-auto mb-4">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-green-400">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-green-400 mb-4 font-medium">Password reset email sent!</p>
            <p className="text-content-tertiary text-sm mb-4">Check your inbox for instructions.</p>
            <button
              onClick={() => {
                setResetSent(false);
                setMode('login');
              }}
              className="text-accent-400 hover:text-accent-300 text-sm font-medium transition-colors"
            >
              Back to Sign In
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="input-field w-full"
              />
            </div>

            {mode !== 'reset' && (
              <div>
                <input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="input-field w-full"
                />
              </div>
            )}

            {mode === 'signup' && (
              <div>
                <input
                  type="password"
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="input-field w-full"
                />
              </div>
            )}

            {mode === 'signup' && (
              <p className="text-sm text-content-secondary">By creating an account you agree to the <a className="underline" href="/terms.html" target="_blank" rel="noreferrer">Terms</a> and <a className="underline" href="/privacy.html" target="_blank" rel="noreferrer">Privacy Policy</a>.</p>
            )}

            {error && (
              <p className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full disabled:opacity-50"
            >
              {loading ? 'Loading...' :
                mode === 'login' ? 'Sign In' :
                mode === 'signup' ? 'Sign Up' :
                'Send Reset Email'
              }
            </button>

            <div className="text-center text-sm space-y-2 text-content-secondary">
              {mode === 'login' && (
                <>
                  <button
                    type="button"
                    onClick={() => setMode('reset')}
                    className="text-accent-400 hover:text-accent-300 transition-colors"
                  >
                    Forgot password?
                  </button>
                  <div>
                    Don't have an account?{' '}
                    <button
                      type="button"
                      onClick={() => setMode('signup')}
                      className="text-accent-400 hover:text-accent-300 font-medium transition-colors"
                    >
                      Sign up
                    </button>
                  </div>
                </>
              )}

              {mode === 'signup' && (
                <div>
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => setMode('login')}
                    className="text-accent-400 hover:text-accent-300 font-medium transition-colors"
                  >
                    Sign in
                  </button>
                </div>
              )}

              {mode === 'reset' && (
                <button
                  type="button"
                  onClick={() => setMode('login')}
                  className="text-accent-400 hover:text-accent-300 transition-colors"
                >
                  Back to Sign In
                </button>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AuthModal;
